import { useState, useEffect, useCallback } from 'react'
import { authService } from '../services'
import { setTokens, clearTokens, getAuthToken } from '../http'
import { toast } from 'react-toastify'
import i18n from '../i18n'
import { saveRefreshToken, clearSecureRefreshToken } from '../helpers/secure-storage'

const INIT_DATA_KEY = 'gg_cached_initData'

function cacheInitData() {
    try {
        const initData = window.Telegram?.WebApp?.initData
        if (initData && initData.length > 20) {
            sessionStorage.setItem(INIT_DATA_KEY, initData)
        }
    } catch (e) { /* sessionStorage may be unavailable */ }
}

export function getCachedInitData() {
    const live = window.Telegram?.WebApp?.initData
    if (live && live.length > 20) return live
    try {
        return sessionStorage.getItem(INIT_DATA_KEY) || ''
    } catch (e) { return '' }
}

export const useAuth = () => {
    const [user, setUser] = useState(null)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        cacheInitData()

        const checkAuth = async () => {
            const token = getAuthToken()

            if (token) {
                try {
                    const response = await authService.getMe()
                    if (!response.user?.siteId) {
                        console.warn('Token valid but user has no siteId, requiring login')
                        clearTokens()
                    } else {
                        if (response.user?.isBanned) {
                            const banToastShown =
                                sessionStorage.getItem('banToastShown')
                            if (!banToastShown) {
                                toast(i18n.t('common.banned.title'), {
                                    type: 'error',
                                })
                                sessionStorage.setItem('banToastShown', 'true')
                            }
                        }
                        setUser(response.user)
                        setIsAuthenticated(true)
                        localStorage.setItem('userSiteId', response.user.siteId)
                        setIsLoading(false)
                        return
                    }
                } catch (error) {
                    console.error('Auth check failed:', error)
                    clearTokens()
                }
            }

            const initData = getCachedInitData()
            if (initData && initData.length > 0) {
                try {
                    const savedSiteId = localStorage.getItem('userSiteId')
                    console.log('🔄 Silent re-auth with initData', { hasSavedSiteId: !!savedSiteId })

                    const response = await authService.telegramAuth({
                        initData,
                        siteId: savedSiteId || undefined,
                    })

                    if (response.success && response.user.siteId) {
                        setTokens(
                            response.tokens.accessToken,
                            response.tokens.refreshToken
                        )
                        saveRefreshToken(response.tokens.refreshToken)
                        setUser(response.user)
                        setIsAuthenticated(true)
                        localStorage.setItem('userSiteId', response.user.siteId)

                        console.log('✅ Silent re-auth successful')
                        setIsLoading(false)
                        return
                    }
                } catch (silentError) {
                    const code = silentError?.response?.data?.code
                    console.warn('Silent re-auth failed:', code || silentError.message)
                    clearTokens()
                }
            }

            setIsAuthenticated(false)
            setIsLoading(false)
        }

        checkAuth()
    }, [])

    const loginWithTelegram = useCallback(async (initData, siteId) => {
        setIsLoading(true)
        setError(null)

        try {
            const response = await authService.telegramAuth({
                initData,
                siteId,
            })

            if (response.success) {
                if (!response.user.siteId) {
                    const err = new Error('Site ID требуется')
                    err.response = { data: { code: 'SITE_ID_REQUIRED', error: 'Site ID требуется' } }
                    throw err
                }

                setTokens(
                    response.tokens.accessToken,
                    response.tokens.refreshToken
                )
                saveRefreshToken(response.tokens.refreshToken)
                setUser(response.user)
                setIsAuthenticated(true)
                localStorage.setItem('userSiteId', response.user.siteId)

                return response
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || 'Ошибка авторизации'
            const errorCode = error.response?.data?.code
            setError(errorMsg)

            if (
                errorCode === 'SITE_ID_TAKEN' ||
                errorMsg.includes('siteId') ||
                errorMsg.includes('используется')
            ) {
                localStorage.removeItem('userSiteId')
            }

            throw error
        } finally {
            setIsLoading(false)
        }
    }, [])

    const logout = useCallback(async () => {
        try {
            await authService.logout()
        } catch (error) {
            console.error('Logout error:', error)
        } finally {
            clearTokens()
            clearSecureRefreshToken()
            setUser(null)
            setIsAuthenticated(false)
        }
    }, [])

    const refreshUser = useCallback(async () => {
        try {
            const response = await authService.getMe()
            if (response.user?.isBanned) {
                const banToastShown = sessionStorage.getItem('banToastShown')
                if (!banToastShown) {
                    toast(i18n.t('common.banned.title'), { type: 'error' })
                    sessionStorage.setItem('banToastShown', 'true')
                }
            }
            setUser(response.user)
            setIsAuthenticated(true)
            // Язык определяется в app.js
            
            return response.user
        } catch (error) {
            console.error('Failed to refresh user:', error)
            throw error
        }
    }, [])

    const updateUserData = useCallback(async (updates) => {
        if (updates) {
            setUser((prevUser) => {
                if (!prevUser) return prevUser
                return {
                    ...prevUser,
                    ...updates,
                }
            })
            return
        }

        try {
            const response = await authService.getMe()
            setUser(response.user)
            return response.user
        } catch (error) {
            console.error('Failed to update user data:', error)
            throw error
        }
    }, [])

    return {
        user,
        isAuthenticated,
        isLoading,
        error,
        loginWithTelegram,
        logout,
        refreshUser,
        updateUserData,
    }
}
